'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { submissionSchema, type SubmissionFormData } from '@/lib/validations/submission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/components/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import { Loader2, ArrowLeft } from 'lucide-react'
import { Logo } from '@/components/brand/logo'
import Link from 'next/link'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'

export default function FormPage() {
  const router = useRouter()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [sameAsResidential, setSameAsResidential] = useState(false)

  const form = useForm<SubmissionFormData>({
    resolver: zodResolver(submissionSchema),
    defaultValues: {
      enterprise_name: '',
      candidate_name: '',
      father_or_husband_name: '',
      gender: undefined,
      email: '',
      phone: '',
      aadhaar_number: '',
      date_of_birth: '',
      age: '',
      name_of_scheme: '',
      residential_address_vill_moh: '',
      residential_address_po: '',
      residential_address_ps: '',
      residential_address_block: '',
      residential_address_district: '',
      residential_address_pincode: '',
      location_of_unit_vill_moh: '',
      location_of_unit_po: '',
      location_of_unit_ps: '',
      location_of_unit_block: '',
      location_of_unit_district: '',
      location_of_unit_pincode: '',
      location_type: undefined,
      category_of_enterprise: undefined,
      social_category: undefined,
      date_of_birth_or_age: '',
      educational_qualification: undefined,
      type_of_organisation: undefined,
      product_name: '',
      ifsc_code: '',
      bank_name: '',
      branch_name: '',
      branch_address: '',
      land_ownership: undefined,
      lease_or_rent_amount: '',
      building_ownership: undefined,
      building_amount: '',
      machine_value: '',
      other_fixed_assets: '',
      total_term_loan_required: '',
      working_capital_loan_required: '',
      total_project_cost: '',
      service_area_bank_name: '',
      service_area_bank_branch_name: '',
      service_area_bank_branch_address: '',
      service_area_bank_branch_city: '',
      service_area_bank_branch_state: '',
      service_area_bank_branch_pincode: '',
      service_area_bank_branch_ifsc_code: '',
      contribution_option: '',
      notes: '',
      website: '', // Honeypot
    },
  })

  // Track form changes
  useEffect(() => {
    const subscription = form.watch(() => {
      const values = form.getValues()
      const hasChanges = Object.entries(values).some(
        ([key, value]) => key !== 'website' && value && value !== ''
      )
      setHasUnsavedChanges(hasChanges)
    })
    return () => subscription.unsubscribe()
  }, [form])

  // Auto-focus first input on mount
  useEffect(() => {
    setTimeout(() => {
      const firstInput = document.querySelector('[name="enterprise_name"]') as HTMLInputElement
      firstInput?.focus()
    }, 100)
  }, [])

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  // Handle "Same as Residential Address" checkbox
  const handleSameAsResidential = (checked: boolean) => {
    setSameAsResidential(checked)
    if (checked) {
      // Copy residential address to unit location
      form.setValue('location_of_unit_vill_moh', form.getValues('residential_address_vill_moh'))
      form.setValue('location_of_unit_po', form.getValues('residential_address_po'))
      form.setValue('location_of_unit_ps', form.getValues('residential_address_ps'))
      form.setValue('location_of_unit_block', form.getValues('residential_address_block'))
      form.setValue('location_of_unit_district', form.getValues('residential_address_district'))
      form.setValue('location_of_unit_pincode', form.getValues('residential_address_pincode'))
    }
  }

  // Fetch bank details from IFSC code
  const handleIFSCChange = async (value: string) => {
    const ifsc = value.toUpperCase().trim()
    form.setValue('ifsc_code', ifsc)

    if (ifsc.length === 11) {
      try {
        const response = await fetch(`https://ifsc.razorpay.com/${ifsc}`)
        if (response.ok) {
          const data = await response.json()
          form.setValue('bank_name', data.BANK || '')
          form.setValue('branch_name', data.BRANCH || '')
          form.setValue('branch_address', data.ADDRESS || '')
        } else {
          // Clear fields if IFSC not found
          form.setValue('bank_name', '')
          form.setValue('branch_name', '')
          form.setValue('branch_address', '')
          toast({
            title: 'IFSC Not Found',
            description: 'Please check the IFSC code and try again.',
            variant: 'destructive',
          })
        }
      } catch (error) {
        console.error('Error fetching IFSC details:', error)
      }
    }
  }

  // Auto-format date of birth and calculate age
  const handleDOBChange = (value: string) => {
    // Remove all non-digits
    let cleaned = value.replace(/\D/g, '')

    // Auto-format with slashes (DD/MM/YYYY)
    if (cleaned.length >= 2) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2)
    }
    if (cleaned.length >= 5) {
      cleaned = cleaned.slice(0, 5) + '/' + cleaned.slice(5, 9)
    }

    form.setValue('date_of_birth', cleaned)

    // Calculate age if full date is entered
    if (cleaned.length === 10) {
      const parts = cleaned.split('/')
      if (parts.length === 3) {
        const day = parseInt(parts[0])
        const month = parseInt(parts[1]) - 1 // JS months are 0-indexed
        const year = parseInt(parts[2])

        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && year > 1900 && year < 2100) {
          const birthDate = new Date(year, month, day)
          const today = new Date()
          let age = today.getFullYear() - birthDate.getFullYear()
          const monthDiff = today.getMonth() - birthDate.getMonth()

          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--
          }

          if (age >= 0 && age < 150) {
            form.setValue('age', age.toString())
          }
        }
      }
    }
  }

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        router.push('/')
      }
    } else {
      router.push('/')
    }
  }

  const onSubmit = async (data: SubmissionFormData) => {
    // Honeypot check
    if (data.website) {
      // Silent fail for bots
      return
    }

    setIsSubmitting(true)

    try {
      console.log('Submitting form data:', data)

      const response = await fetch('/api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      console.log('Response status:', response.status)
      console.log('Response ok:', response.ok)

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to submit' }))
        console.error('API Error:', error)
        throw new Error(error.error || 'Failed to submit form')
      }

      const result = await response.json()
      console.log('API Result:', result)

      // Show brief success toast
      toast({
        title: 'Success!',
        description: 'Redirecting to confirmation page...',
        duration: 2000,
      })

      form.reset()
      setHasUnsavedChanges(false)

      // Redirect to success page with submission ID
      setTimeout(() => {
        router.push(`/form/success?id=${result.id}`)
      }, 500)
    } catch (error: any) {
      console.error('Form submission error:', error)
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit. Please try again.',
        variant: 'destructive',
        duration: 10000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-muted-50 dark:bg-ink-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-display font-semibold">
              Application Form
            </CardTitle>
            <CardDescription>
              Fill out the form below and we will get back to you soon.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Form Errors Display */}
                {Object.keys(form.formState.errors).length > 0 && (
                  <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                    <h4 className="font-semibold text-destructive mb-2">Please fix the following errors:</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      {Object.entries(form.formState.errors).map(([field, error]) => (
                        <li key={field} className="text-destructive">
                          {error?.message?.toString() || `${field} has an error`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Honeypot field (hidden) */}
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem className="hidden">
                      <FormControl>
                        <Input type="text" {...field} tabIndex={-1} autoComplete="off" />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
                  
                  <FormField
                    control={form.control}
                    name="enterprise_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name of the Enterprise/Entity *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="candidate_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name of the Candidate *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="father_or_husband_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Father/Husband Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gender</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email (Optional)</FormLabel>
                          <FormControl>
                            <Input type="email" {...field} placeholder="your@email.com" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input type="tel" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="aadhaar_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Aadhaar Number (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter 12-digit Aadhaar number"
                            maxLength={12}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="date_of_birth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth (DD/MM/YYYY)</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="DD/MM/YYYY"
                              maxLength={10}
                              onChange={(e) => handleDOBChange(e.target.value)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="age"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Age</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Auto-calculated or enter manually"
                              type="number"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="name_of_scheme"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name of Scheme (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter the scheme name you are applying for"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Residential Address */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Residential Address</h3>
                  
                  <FormField
                    control={form.control}
                    name="residential_address_vill_moh"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Village/Mohalla</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="residential_address_po"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Post Office</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="residential_address_ps"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Police Station</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="residential_address_block"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Block</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="residential_address_district"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>District</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="residential_address_pincode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pincode</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Unit Location */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Location of Unit</h3>

                  {/* Same as Residential Address Checkbox */}
                  <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-md">
                    <Checkbox
                      id="sameAsResidential"
                      checked={sameAsResidential}
                      onCheckedChange={handleSameAsResidential}
                    />
                    <label
                      htmlFor="sameAsResidential"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Same as Residential Address
                    </label>
                  </div>

                  <FormField
                    control={form.control}
                    name="location_of_unit_vill_moh"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Village/Mohalla</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="location_of_unit_po"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Post Office</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="location_of_unit_ps"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Police Station</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="location_of_unit_block"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Block</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="location_of_unit_district"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>District</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="location_of_unit_pincode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pincode</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="location_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Location Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select location type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Urban">Urban</SelectItem>
                              <SelectItem value="Rural">Rural</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Enterprise Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Enterprise Details</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="category_of_enterprise"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category of Enterprise</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                              <SelectItem value="Service Sector">Service Sector</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="social_category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Social Category</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="SC">SC</SelectItem>
                              <SelectItem value="ST">ST</SelectItem>
                              <SelectItem value="OBC">OBC</SelectItem>
                              <SelectItem value="Female">Female</SelectItem>
                              <SelectItem value="Handicapped">Handicapped</SelectItem>
                              <SelectItem value="General">General</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="date_of_birth_or_age"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth or Age</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="DD/MM/YYYY or Age" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="educational_qualification"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Educational Qualification</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select qualification" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="8th">8th</SelectItem>
                              <SelectItem value="10th">10th</SelectItem>
                              <SelectItem value="12th">12th</SelectItem>
                              <SelectItem value="Graduation">Graduation</SelectItem>
                              <SelectItem value="Post Graduation">Post Graduation</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="type_of_organisation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type of Organisation</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select organisation type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Proprietorship">Proprietorship</SelectItem>
                            <SelectItem value="Partnership">Partnership</SelectItem>
                            <SelectItem value="Private Limited Company">Private Limited Company</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="product_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Ownership & Assets */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Ownership & Assets</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="land_ownership"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Land Ownership</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select ownership" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Own">Own</SelectItem>
                              <SelectItem value="Leased">Leased</SelectItem>
                              <SelectItem value="Rent">Rent</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lease_or_rent_amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lease/Rent Amount (if applicable)</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="building_ownership"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Building Ownership</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select ownership" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Own">Own</SelectItem>
                              <SelectItem value="Rent">Rent</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="building_amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Building Amount</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="machine_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Machine Value</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="other_fixed_assets"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Other Fixed Assets</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Financial Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold border-b pb-2">Financial Details</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="total_term_loan_required"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Total Term Loan Required</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="working_capital_loan_required"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Working Capital Loan Required</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="total_project_cost"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Total Project Cost</FormLabel>
                          <FormControl>
                            <Input {...field} type="number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="ifsc_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>IFSC Code</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="e.g., SBIN0001234"
                              maxLength={11}
                              onChange={(e) => handleIFSCChange(e.target.value)}
                              className="uppercase"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Auto-filled Bank Details */}
                  {form.watch('bank_name') && (
                    <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                      <p className="text-sm font-semibold">Bank Details (Auto-filled)</p>
                      <div className="grid md:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="font-medium">Bank:</span> {form.watch('bank_name')}
                        </div>
                        <div>
                          <span className="font-medium">Branch:</span> {form.watch('branch_name')}
                        </div>
                        <div className="md:col-span-2">
                          <span className="font-medium">Address:</span> {form.watch('branch_address')}
                        </div>
                      </div>
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="contribution_option"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contribution Option</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter contribution option" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Additional Notes */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Additional Notes (Optional)</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={4} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-brand-500 hover:bg-brand-600"
                  >
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

